/**
 * Credits Service - Frontend API for credits and billing
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://pinak.ai';

export interface UsageSummary {
  user_id: string;
  tier: string;
  tier_name: string;
  monthly_allowance: number;
  credits_used: number;
  additional_credits: number;
  remaining_credits: number;
  usage_percentage: number;
  available_models: string[];
  billing_cycle_start?: string;
}

export interface UsageEntry {
  id: string;
  request_type: string;
  model_used: string;
  input_tokens: number;
  output_tokens: number;
  input_cost: number;
  output_cost: number;
  total_cost: number;
  infrastructure_cost: number;
  execution_time_ms: number;
  prompt?: string;
  created_at: string;
  success: boolean;
}

export interface RateLimitResult {
  allowed: boolean;
  reason?: string;
  remaining_credits: number;
  tier: string;
  requests_remaining?: number;
}

export interface PricingTier {
  name: string;
  price: number | null;
  monthly_credits: number | null;
  features: string[];
  models: string[];
  max_requests_per_day: number | null;
}

export interface StripeConfig {
  publishable_key: string;
  available: boolean;
}

/**
 * Get the user's credits usage summary
 */
export async function getUsageSummary(userId: string): Promise<UsageSummary | null> {
  try {
    const response = await fetch(`${API_BASE}/api/credits/summary`, {
      headers: {
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.error('Failed to get usage summary:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting usage summary:', error);
    return null;
  }
}

/**
 * Get the user's usage history
 */
export async function getUsageHistory(
  userId: string, 
  limit: number = 50
): Promise<UsageEntry[]> {
  try {
    const response = await fetch(`${API_BASE}/api/credits/history?limit=${limit}`, {
      headers: {
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.error('Failed to get usage history:', response.status);
      return [];
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting usage history:', error);
    return [];
  }
}

/**
 * Check rate limit before making a request
 */
export async function checkRateLimit(userId?: string): Promise<RateLimitResult> {
  try {
    const response = await fetch(`${API_BASE}/api/rate-limit`, {
      headers: {
        'X-User-Id': userId || '',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      // Allow request on error
      return {
        allowed: true,
        reason: 'Rate limit check failed',
        remaining_credits: 999,
        tier: 'unknown',
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking rate limit:', error);
    return {
      allowed: true,
      reason: 'Rate limit check error',
      remaining_credits: 999,
      tier: 'unknown',
    };
  }
}

/**
 * Get all pricing tiers
 */
export async function getPricingTiers(): Promise<Record<string, PricingTier>> {
  try {
    const response = await fetch(`${API_BASE}/api/credits/tiers`);
    
    if (!response.ok) {
      console.error('Failed to get pricing tiers:', response.status);
      return {};
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting pricing tiers:', error);
    return {};
  }
}

/**
 * Get Stripe configuration
 */
export async function getStripeConfig(): Promise<StripeConfig> {
  try {
    const response = await fetch(`${API_BASE}/api/stripe/config`);
    
    if (!response.ok) {
      console.error('Failed to get Stripe config:', response.status);
      return { publishable_key: '', available: false };
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting Stripe config:', error);
    return { publishable_key: '', available: false };
  }
}

/**
 * Create a Stripe checkout session for Pro subscription
 */
export async function createCheckoutSession(
  userId: string,
  email: string,
  priceType: 'pro_monthly' | 'pro_yearly' | 'credits_10' | 'credits_25' | 'credits_50'
): Promise<{ session_id: string; url: string } | null> {
  try {
    const response = await fetch(`${API_BASE}/api/stripe/checkout`, {
      method: 'POST',
      headers: {
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        price_type: priceType,
        email: email,
      }),
    });
    
    if (!response.ok) {
      console.error('Failed to create checkout session:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return null;
  }
}

/**
 * Create a Stripe customer portal session
 */
export async function createPortalSession(userId: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE}/api/stripe/portal`, {
      method: 'POST',
      headers: {
        'X-User-Id': userId,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.error('Failed to create portal session:', response.status);
      return null;
    }
    
    const data = await response.json();
    return data.url || null;
  } catch (error) {
    console.error('Error creating portal session:', error);
    return null;
  }
}

/**
 * Format cost for display
 */
export function formatCost(cost: number): string {
  if (cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(2)}`;
}

/**
 * Format tokens for display
 */
export function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

/**
 * Get model display name
 */
export function getModelDisplayName(model: string): string {
  const names: Record<string, string> = {
    'gemini-2.0-flash-preview': 'Gemini Flash',
    'gemini-2.0-pro-preview': 'Gemini Pro',
    'claude-3-5-sonnet': 'Claude 3.5',
    'gpt-4o': 'GPT-4o',
    'claude-3-opus': 'Claude Opus',
  };
  return names[model] || model;
}
